// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/** Number/label formatting — pure, unit-tested. */

import type { Measurement } from './types';

/** Angle in degrees, one decimal. */
export function formatDeg(deg: number): string {
  if (!Number.isFinite(deg)) return '—';
  return `${deg.toFixed(1)}°`;
}

/** Signed angle, one decimal, with an explicit + for positive. */
export function formatSignedDeg(deg: number): string {
  if (!Number.isFinite(deg)) return '—';
  const s = deg.toFixed(1);
  return deg > 0 ? `+${s}°` : `${s}°`;
}

/**
 * The grade above which a tan-derived number is a blow-up towards vertical, not
 * a measurement. Keeps the string short enough for a fixed-width readout.
 */
const GRADE_LIMIT = 100000;

/** Percent grade, sensibly rounded. */
export function formatGrade(pct: number): string {
  if (!Number.isFinite(pct)) return '—';
  const a = Math.abs(pct);
  if (a >= GRADE_LIMIT) return '—';
  if (a < 10) return `${pct.toFixed(1)} %`;
  return `${Math.round(pct)} %`;
}

/** "Level" when within tolerance, else how far off and which way (level mode). */
export function describeLevel(fromHorizontal: number, tolerance = 0.2): string {
  if (fromHorizontal <= tolerance) return 'Level';
  return `${fromHorizontal.toFixed(1)}° off level`;
}

/** A one-line, copy-able summary of a captured measurement. */
export function summaryLine(m: Measurement): string {
  const bits = [`${formatDeg(m.angle)}`, `${formatGrade(m.grade)} grade`, `${m.roofPitch} pitch`, m.ratio];
  const what = m.mode === 'level' ? 'Tilt' : m.mode === 'angle' ? 'Slope' : 'Angle';
  return `${m.label || what}: ${bits.join(' · ')} — measured with Tiltwell (tiltwell.benrichardson.dev)`;
}

/** Clock-style timestamp for a measurement row. */
export function formatClock(at: number): string {
  const d = new Date(at);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
